import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import Logo from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import { Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const Login = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', formData);

      console.log('LOGIN RESPONSE:', response?.data);

      const token = response?.data?.token;
      const user = response?.data?.user;

      if (!token || !user) {
        toast.error(response?.data?.detail || 'Login response invalid');
        return;
      }

      if (rememberMe) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(user));
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      toast.success(t('welcomeToUniverse'));

      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('LOGIN ERROR:', error?.response?.data || error);

      toast.error(
        error?.response?.data?.detail ||
        error?.message ||
        t('loginFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative">
      <CosmicBackground />
      <div
        className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-violet-900/20 via-slate-900 to-slate-950 pointer-events-none"
        style={{ zIndex: 1 }}
      />

      <div className="fixed top-4 right-4 z-20">
        <LanguageSelector />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md px-6"
      >
        <div className="glass-card p-8">
          <div className="flex flex-col items-center mb-8">
            <Logo size="lg" showText={false} className="mb-4" />
            <h1 className="font-heading text-3xl font-bold text-white">
              {t('welcomeBack')}
            </h1>
            <p className="text-slate-400 mt-2">{t('enterCosmicRealm')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {t('email')}
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {t('password')}
              </label>

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="flex justify-between items-center mt-3">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="accent-violet-500"
                  />
                  {language === 'ro' ? 'Ține-mă conectat' : 'Keep me signed in'}
                </label>

                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-violet-400 hover:text-violet-300"
                >
                  {language === 'ro' ? 'Ai uitat parola?' : 'Forgot password?'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full font-medium hover:from-violet-500 hover:to-purple-500 disabled:opacity-50"
            >
              {loading ? t('signingIn') : t('signIn')}
            </button>
          </form>

          <p className="mt-6 text-center text-slate-400 text-sm">
            {t('newToUniverse')}{' '}
            <Link
              to="/signup"
              className="text-violet-400 hover:text-violet-300 font-medium"
            >
              {t('createAccount')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
